import { Box, Typography } from "@mui/material";
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from "react";
import { isMobileOnly } from "react-device-detect";
import { isNullOrEmpty, SELLER, UserStats } from "../../common/common";
import { ReceiverDetailsContext } from "../../contexts/receiverDetailsContext";
import { MessageWarnings, messageWarnings } from "../warnings/warnings";

interface ChatWarningProps {
  onAnimationComplete: any;
  adminIsBlocked: any;
  messageLimitReached?: boolean;
}

const ChatWarning = ({ onAnimationComplete, adminIsBlocked, messageLimitReached = false }: ChatWarningProps, ref: any) => {
  const { receiverDetails } = useContext<any>(ReceiverDetailsContext);
  const [warningStatus, setWarningStatus] = useState<MessageWarnings>(messageWarnings.USER_DEFAULT);
  const [isWarFixedRef, setIsWarFixedRef] = useState<boolean>(false);
  const warRef: any = useRef<any>();
  const shouldAnimateRef: any = useRef<boolean>(false);
  const setMessageWarningDetails = () => {
    if (isNullOrEmpty(receiverDetails) || (!isNullOrEmpty(receiverDetails) && isNullOrEmpty(receiverDetails.name))) {
      setIsWarFixedRef(true);
      setWarningStatus(receiverDetails.entityType === 2 ? messageWarnings.STORE_ACCOUNT_DELETED : messageWarnings.ACCOUNT_DELETED);
    } else if (adminIsBlocked) {
      setIsWarFixedRef(true);
      setWarningStatus(receiverDetails.entityType === 1 ? messageWarnings.ADMIN_IS_BLOCKED : messageWarnings.ADMIN_IS_BLOCKED_BY_STORE);
    } else if (!SELLER && receiverDetails.entityType === 1) {
      switch (receiverDetails.followingStatus) {
        case UserStats.NEITHER_USER_IS_FOLLOWING:
          shouldAnimateRef.current = true;
          setIsWarFixedRef(true);
          setWarningStatus(messageWarnings.NEITHER_FOLLOWING);
          break;
        case UserStats.LOGGED_IN_USER_IS_FOLLOWING:
          shouldAnimateRef.current = true;
          setIsWarFixedRef(true);
          setWarningStatus(messageWarnings.LOGGED_IN_USER_IS_FOLLOWING);
          break;
        case UserStats.USER_IS_FOLLOWING:
          setWarningStatus(messageWarnings.USER_IS_FOLLOWING);
          setIsWarFixedRef(true);
          break;
        default:
          setWarningStatus(messageWarnings.USER_DEFAULT);
          setIsWarFixedRef(false);
          break;
      }
    } else if (!SELLER && receiverDetails.entityType === 2) {
      if (receiverDetails.status === 1) {
        setWarningStatus(messageWarnings.USER_DEFAULT);
        setIsWarFixedRef(false);
      } else if (messageLimitReached) {
        shouldAnimateRef.current = true;
        setWarningStatus(messageWarnings.LIMIT_MESSAGE_REACHED);
        setIsWarFixedRef(true);
      } else {
        setWarningStatus(messageWarnings.STORE_DEFAULT);
        setIsWarFixedRef(true);
      }
    }
  };

  useImperativeHandle(ref, () => ({
    getWarningHeight: () => {
      return warRef.current.offsetHeight;
    },
    isWarningFixed: () => {
      return isWarFixedRef;
    },
    showFollowingWarning: () => {
      const currFollowed: MessageWarnings = messageWarnings.JUST_FOLLLOWED;
      currFollowed.message = currFollowed.message.replace("[FullName]", receiverDetails.name);
      shouldAnimateRef.current = true;
      setWarningStatus(currFollowed);
      setTimeout(() => {
        shouldAnimateRef.current = true;
        setMessageWarningDetails();
      }, 4000);
    },
    resetWarning: () => {
      setMessageWarningDetails();
    },
  }));

  useEffect(() => {
    setMessageWarningDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setMessageWarningDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageLimitReached]);

  useEffect(() => {
    if (shouldAnimateRef.current) {
      if (document.getElementsByClassName("chatWarnings")[0]) {
        document.getElementsByClassName("chatWarnings")[0].classList.remove("chatWarningAnimation");
        document.getElementsByClassName("chatWarnings")[0].classList.add("chatWarningAnimation");
      }
      setTimeout(() => {
        onAnimationComplete && onAnimationComplete();
        if (document.getElementsByClassName("chatWarnings")[0]) {
          document.getElementsByClassName("chatWarnings")[0].classList.remove("chatWarningAnimation");
        }
      }, 300);
      shouldAnimateRef.current = false;
    } else {
      onAnimationComplete && onAnimationComplete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warningStatus]);

  return (
    <Box ref={warRef} className={`chatWarnings display-flex flex-direction-row pad-16 align-items-center bor-t-r-b-l-4 ${isWarFixedRef ? "pos-fixed chatWarning" : "chatWarningFloating"} ${warningStatus.bgClass}`}>
      {!isMobileOnly && warningStatus.icon}
      <Typography variant="body1" className={`pad-l-8 ${warningStatus.textClass}`}>
        {warningStatus.message}
      </Typography>
    </Box>
  );
};

export default forwardRef(ChatWarning);
